import type {
    EventName, EventProps, ProviderProps
} from '@3plearning/mx-teacher-analytics';
import { useCallback } from 'react';

import type { AppEvents } from './events';
import {
    ActivitiesCourseFilterProps,
    ChallengesCourseFilterProps,
    DynamicFilterClickProps,
    TrackAssignmentConfirmedProps,
    TrackAssignmentFlowStartedProps,
    TrackGridCellClickProps,
    TrackGridCheckboxClickProps,
    TrackSpotlightClosedProps,
    TrackSpotlightShownProps,
    TrackViewResultsClickProps
} from './tracking.types';
import { useAnalyticsClient } from './use-analytics-client';
import type { ClassContext } from './use-class-context';
import { useClassContext } from './use-class-context';
import { useAppSelector } from '@config/react-redux/hooks';
import { selectClassGroupFilterGroupSelectedId } from '@containers/class-group-filters/class-group-filters-slice';
import { WHOLE_CLASS_GROUP_ID } from '@containers/class-group-filters/class-group-filters.constants';

const ASSIGN_REVIEW_TAB = 'assign_review_tab';

type DynamicFilterEvent = 'filter.dynamic' | 'ui.scroll-to-top' | 'ui.view-key';

type ViewResultsEvent =
    | 'viewresults.button'
    | 'viewresults.modal-close-icon'
    | 'viewresults.modal-close-button'
    | 'viewresults.modal-student-selector'
    | 'viewresults.modal-previous-activity'
    | 'viewresults.modal-next-activity';

const dynamicFilterEventByType: Record<DynamicFilterClickProps['type'], DynamicFilterEvent> = {
    gridViewOptions: 'filter.dynamic',
    iconView: 'filter.dynamic',
    scrollToTop: 'ui.scroll-to-top',
    viewKey: 'ui.view-key'
};
const viewResultsEventByType: Record<TrackViewResultsClickProps['type'], ViewResultsEvent> = {
    viewResultsButton: 'viewresults.button',
    viewResultsModalCloseIcon: 'viewresults.modal-close-icon',
    viewResultsModalCloseButton: 'viewresults.modal-close-button',
    viewResultsModalStudentSelector: 'viewresults.modal-student-selector',
    viewResultsModalPreviousActivity: 'viewresults.modal-previous-activity',
    viewResultsModalNextActivity: 'viewresults.modal-next-activity'
};

type TrackOptions = {
    // Overrides the store when an event fires before a filter change is dispatched.
    classContext?: ClassContext
    providerProps?: ProviderProps
};

const getAssignmentGroupProps = (groupId: number | null) => ({
    group_id: groupId?.toString(),
    scope: groupId === WHOLE_CLASS_GROUP_ID ? 'class' as const : 'group' as const
});

export const useTracking = () => {
    const client = useAnalyticsClient();
    const selectedGroupId = useAppSelector(selectClassGroupFilterGroupSelectedId);
    const classContext = useClassContext();
    const track = useCallback(<K extends EventName<AppEvents>>(
        name: K,
        props: EventProps<AppEvents, K>,
        options: TrackOptions = {}
    ) => {
        return client?.track(name, {
            ...(options.classContext || classContext),
            ...props
        }, options.providerProps);
    }, [client, classContext]);
    const trackCourseTypeView = useCallback((path: string) => {
        return track('coursetype.view', { path });
    }, [track]);
    const trackModuleClicked = useCallback((moduleId: number, moduleName: string) => {
        return track('module.clicked', {
            module_id: moduleId,
            module_name: moduleName,
            click_type: ASSIGN_REVIEW_TAB
        });
    }, [track]);
    const trackAssignmentFlowStarted = useCallback((props: TrackAssignmentFlowStartedProps) => {
        track('assignment.flow-started', {
            type: props.type,
            count: props.count,
            ...getAssignmentGroupProps(selectedGroupId)
        });
    }, [track, selectedGroupId]);
    const trackAssignmentConfirmed = useCallback((props: TrackAssignmentConfirmedProps) => {
        track('assignment.confirmation', {
            type: props.type,
            count: props.count,
            is_assigned_activities_modal: props.isAssignedActivitiesModal,
            course_id: props.courseId,
            is_assign_to_all_students: props.isAssignToAllStudents,
            ...getAssignmentGroupProps(selectedGroupId)
        });
    }, [track, selectedGroupId]);
    const trackAssignmentUnassigned = useCallback(() => {
        track('assignment.confirmation', {
            type: 'unassign',
            ...getAssignmentGroupProps(selectedGroupId)
        });
    }, [track, selectedGroupId]);
    const trackAssignmentModified = useCallback(() => {
        track('assignment.confirmation', {
            type: 'modify',
            ...getAssignmentGroupProps(selectedGroupId)
        });
    }, [track, selectedGroupId]);
    const trackClassChanged = useCallback((classroomId: string) => {
        track('filter.class-changed', { value: classroomId }, {
            classContext: {
                classId: classroomId,
                groupId: WHOLE_CLASS_GROUP_ID.toString()
            }
        });
    }, [track]);
    // Group names can contain student names, so they go to teacher-totter only.
    const trackGroupChanged = useCallback((groupId: string, classroomId: string, groupName?: string) => {
        track('filter.group-changed', {
            value: groupId,
            classroom_id: classroomId
        }, {
            classContext: {
                classId: classroomId,
                groupId
            },
            providerProps: { teacherTotter: { groupName: groupName || null } }
        });
    }, [track]);
    const trackDynamicFilterClick = useCallback((props: DynamicFilterClickProps) => {
        const event = dynamicFilterEventByType[props.type];

        if (event === 'filter.dynamic') {
            track(event, { type: props.type as 'gridViewOptions' | 'iconView' });

            return;
        }

        track(event, {});
    }, [track]);
    const trackChallengesCourseFilterClick = useCallback((props: ChallengesCourseFilterProps) => {
        track('filter.challenges-course', {
            type: props.type,
            value: props.value
        });
    }, [track]);
    const trackActivitiesCourseFilterClick = useCallback((props: ActivitiesCourseFilterProps) => {
        track('filter.activities-course', { type: props.type });
    }, [track]);
    const trackGridCheckboxClick = useCallback((props: TrackGridCheckboxClickProps) => {
        track('grid.checkbox-click', {
            type: props.type,
            is_checked: props.isChecked,
            topic_id: props.topicId,
            activity_id: props.activityId,
            student_user_profile_id: props.studentUserProfileId
        });
    }, [track]);
    const trackGridCellClick = useCallback((props: TrackGridCellClickProps) => {
        track('grid.cell-click', {
            type: props.type,
            topic_id: props.topicId,
            activity_id: props.activityId,
            activity_type: props.activityType,
            student_user_profile_id: props.studentUserProfileId,
            is_selected: props.isSelected
        });
    }, [track]);
    const trackStudentScrollClick = useCallback(() => {
        track('student.scroll', {});
    }, [track]);
    const trackViewResultsClick = useCallback((props: TrackViewResultsClickProps) => {
        track(viewResultsEventByType[props.type], {});
    }, [track]);
    const trackSpotlightShown = useCallback((props: TrackSpotlightShownProps) => {
        track('ui.spotlight-shown', {
            spotlight: props.spotlight,
            version: props.version
        });
    }, [track]);
    const trackSpotlightClosed = useCallback((props: TrackSpotlightClosedProps) => {
        track('ui.spotlight-closed', {
            spotlight: props.spotlight,
            version: props.version,
            reason: props.reason
        });
    }, [track]);

    return {
        trackCourseTypeView,
        trackModuleClicked,
        trackAssignmentFlowStarted,
        trackAssignmentConfirmed,
        trackAssignmentUnassigned,
        trackAssignmentModified,
        trackClassChanged,
        trackGroupChanged,
        trackDynamicFilterClick,
        trackChallengesCourseFilterClick,
        trackActivitiesCourseFilterClick,
        trackGridCheckboxClick,
        trackGridCellClick,
        trackStudentScrollClick,
        trackViewResultsClick,
        trackSpotlightShown,
        trackSpotlightClosed
    };
};
